import type { ActivationGroup } from '@/features/activationGroup/types'
import type { ExtendedBid } from '@/features/bidding/types/bid'

/**
 * Filters bids to only include those whose activation group UUID matches one of the eligible activation groups.
 *
 * This function ensures that only bids for activation groups that are relevant to the current flow
 * are included. For example, when handling C_AND_I groups, BESS group bids should be excluded.
 *
 * Uses Set-based filtering for O(1) lookup performance.
 *
 * @param bids - The bids to filter
 * @param eligibleActivationGroups - The activation groups to filter by
 * @returns Array of bids that match the eligible activation groups, or empty array if inputs are null/undefined/empty
 */
export function filterBidsByEligibleActivationGroups(
  bids: ExtendedBid[] | null | undefined,
  eligibleActivationGroups: ActivationGroup[] | null | undefined,
): ExtendedBid[] {
  if (!bids || !eligibleActivationGroups) {
    return []
  }

  const activationGroupUuids = new Set(eligibleActivationGroups.map((ag) => ag.uuid))
  return bids.filter((bid) => activationGroupUuids.has(bid.activationGroupUuid))
}
